/**
 * video-carousel.js
 *
 * Initialises the Video Carousel section. Reuses the shared Swiper CDN loader
 * established by custom-carousel.js (window.__ccSwiperPromise) so Swiper is
 * fetched only once per page, regardless of which carousel script runs first.
 *
 * Behaviour per section instance:
 *  - Swiper with centeredSlides so the active slide appears centred and scaled.
 *  - Only the currently active slide video plays; all others are paused.
 *  - Clicking a slide opens a per-block modal with the same video in a larger view.
 *  - Modals animate in/out via CSS transitions driven by the [open] attribute.
 */

(function () {
    "use strict";

    const BREAKPOINT = 750; // px — matches Shopify Focal theme
    const SWIPER_CSS =
        "https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.css";
    const SWIPER_JS =
        "https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.js";

    // ─── Swiper loader (shared with custom-carousel.js) ─────────────────────────

    function loadSwiper() {
        if (window.__ccSwiperPromise) return window.__ccSwiperPromise;

        window.__ccSwiperPromise = new Promise((resolve, reject) => {
            if (window.Swiper) {
                resolve(window.Swiper);
                return;
            }

            if (!document.querySelector(`link[href="${SWIPER_CSS}"]`)) {
                const link = document.createElement("link");
                link.rel = "stylesheet";
                link.href = SWIPER_CSS;
                document.head.appendChild(link);
            }

            const existing = document.querySelector(`script[src="${SWIPER_JS}"]`);
            if (existing) {
                existing.addEventListener("load", () => resolve(window.Swiper));
                existing.addEventListener("error", reject);
                return;
            }

            const script = document.createElement("script");
            script.src = SWIPER_JS;
            script.addEventListener("load", () => resolve(window.Swiper));
            script.addEventListener("error", reject);
            document.head.appendChild(script);
        });

        return window.__ccSwiperPromise;
    }

    // ─── Video helpers ───────────────────────────────────────────────────────────

    /**
     * Dismiss the blurred poster overlay for a video once it has enough data.
     */
    function bindPosterDismissal(video) {
        if (!video || video.dataset.vcPosterBound) return;
        video.dataset.vcPosterBound = "1";
        const poster = video
            .closest(".vc-slide__video-wrapper")
            ?.querySelector(".vc-slide__blur-poster");
        if (!poster) return;
        function dismiss() {
            poster.classList.add("is-hidden");
            // Remove from DOM after the CSS transition so it no longer blocks paint.
            poster.addEventListener("transitionend", () => poster.remove(), {
                once: true,
            });
        }
        if (video.readyState >= 3) {
            dismiss();
            return;
        } // HAVE_FUTURE_DATA or better
        video.addEventListener("canplay", dismiss, { once: true });
    }

    /**
     * Pause every carousel-slide video in the given Swiper element, then play
     * the one inside `.swiper-slide-active`. Safe with Swiper loop clones.
     */
    function playActive(swiperEl) {
        swiperEl.querySelectorAll(".swiper-slide video").forEach((v) => v.pause());
        const active = swiperEl.querySelector(".swiper-slide-active video");
        if (active) {
            bindPosterDismissal(active);
            active.play().catch(() => { });
        }
    }

    // ─── Modal helpers ───────────────────────────────────────────────────────────

    function openModal(modal, swiper) {
        // Pause carousel autoplay and active slide video while modal is open.
        if (swiper.autoplay && swiper.params.autoplay) swiper.autoplay.stop();
        swiper.el.querySelectorAll(".swiper-slide video").forEach((v) => v.pause());

        modal.style.display = "flex";
        // Double rAF ensures display is painted before the CSS transition begins.
        // vid.play() is called INSIDE the rAF so the video starts only once
        // the [open] attribute is set (opacity > 0). Playing while opacity:0 can
        // suppress loop restarts in Chromium-based browsers.
        const vid = modal.querySelector("video");
        requestAnimationFrame(() =>
            requestAnimationFrame(() => {
                modal.setAttribute("open", "");
                if (vid) {
                    vid.loop = true;
                    vid.play().catch(() => { });
                }
            }),
        );
    }

    function closeModal(modal, swiper) {
        const vid = modal.querySelector("video");
        if (vid) {
            vid.pause();
            vid.currentTime = 0;
        }

        modal.removeAttribute("open");

        // Wait for the content fade-out transition to finish before hiding.
        const content = modal.querySelector(".vc-modal__content");
        function onEnd() {
            content.removeEventListener("transitionend", onEnd);
            modal.style.display = "";
            if (swiper.autoplay && swiper.params.autoplay) swiper.autoplay.start();
            playActive(swiper.el);
        }
        content.addEventListener("transitionend", onEnd);
    }

    // ─── Per-section initialisation ─────────────────────────────────────────────

    function initSection(section) {
        if (section.dataset.vcInit) return;
        section.dataset.vcInit = "1";

        const carousel = section.querySelector(".vc-carousel");
        const swiperEl = section.querySelector(".vc-swiper");
        const prevBtn = section.querySelector(".vc-prev");
        const nextBtn = section.querySelector(".vc-next");
        if (!carousel || !swiperEl) return;

        const d = carousel.dataset;

        const loop = d.vcLoop === "true";
        const autoplayEnabled = d.vcAutoplay === "true";
        const autoplayDelay = parseFloat(d.vcAutoplayDelay) || 4000;
        const speed = parseFloat(d.vcSpeed) || 600;
        const slidesMobile = parseFloat(d.vcSlidesMobile) || 1;
        const slidesDesktop = parseFloat(d.vcSlidesDesktop) || 3;
        const gapMobile = parseFloat(d.vcGapMobile) || 16;
        const gapDesktop = parseFloat(d.vcGapDesktop) || 24;
        const mobileArrows = d.vcMobileArrows === "true";
        const desktopArrows = d.vcDesktopArrows === "true";
        const mobileDraggable = d.vcMobileDraggable === "true";
        const desktopDraggable = d.vcDesktopDraggable === "true";

        const isDesktop = () =>
            window.matchMedia(`(min-width: ${BREAKPOINT}px)`).matches;

        // Swiper loop requires slides.length > slidesPerView (strictly).
        // Cap here so loop never silently breaks when the merchant has few videos.
        const slideCount = swiperEl.querySelectorAll('.swiper-slide').length;
        const effectiveMobile  = loop ? Math.min(slidesMobile,  Math.max(1, slideCount - 1)) : slidesMobile;
        const effectiveDesktop = loop ? Math.min(slidesDesktop, Math.max(1, slideCount - 1)) : slidesDesktop;

        function setArrows() {
            const show = isDesktop() ? desktopArrows : mobileArrows;
            if (prevBtn) prevBtn.hidden = !show;
            if (nextBtn) nextBtn.hidden = !show;
        }

        const swiper = new window.Swiper(swiperEl, {
            speed: speed,
            loop: loop && slideCount > 1,
            centeredSlides: true,
            roundLengths: true,
            /* observer: true,
            observeParents: true, */
            slidesPerView: 'auto',
            spaceBetween: gapMobile,
            allowTouchMove: isDesktop() ? desktopDraggable : mobileDraggable,

            navigation: {
                prevEl: prevBtn,
                nextEl: nextBtn,
                disabledClass: "vc-nav--disabled",
            },

            autoplay: autoplayEnabled
                ? {
                    delay: autoplayDelay,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true,
                }
                : false,

            breakpoints: {
                [BREAKPOINT]: {
                    slidesPerView: 'auto',
                    spaceBetween: gapDesktop,
                    allowTouchMove: desktopDraggable,
                },
            },

            on: {
                init(sw) {
                    setArrows();
                    playActive(sw.el);
                },
                slideChangeTransitionEnd(sw) {
                    playActive(sw.el);
                },
            },
        });

        // Keep arrow visibility and touch-move in sync with viewport changes.
        window
            .matchMedia(`(min-width: ${BREAKPOINT}px)`)
            .addEventListener("change", () => {
                setArrows();
                swiper.params.allowTouchMove = isDesktop()
                    ? desktopDraggable
                    : mobileDraggable;
                swiper.update();
            });

        // ── Slide click → open modal ─────────────────────────────────────────────
        // Use event delegation on the swiper so loop-cloned slides also trigger modals.
        let vcStartX = 0, vcStartY = 0;
        swiperEl.addEventListener('pointerdown', e => { vcStartX = e.clientX; vcStartY = e.clientY; });
        swiperEl.addEventListener('click', e => {
            if (Math.abs(e.clientX - vcStartX) > 6 || Math.abs(e.clientY - vcStartY) > 6) return;
            const slide = e.target.closest('.vc-slide');
            if (!slide) return;
            const modal = section.querySelector(`.vc-modal[data-block-id="${slide.dataset.blockId}"]`);
            if (modal) openModal(modal, swiper);
        });

        // ── Modal close triggers ─────────────────────────────────────────────────
        section.querySelectorAll(".vc-modal").forEach((modal) => {
            modal
                .querySelector(".vc-modal__overlay")
                ?.addEventListener("click", () => closeModal(modal, swiper));
            modal
                .querySelector(".vc-modal__close")
                ?.addEventListener("click", () => closeModal(modal, swiper));
            modal
                .querySelector(".vc-modal__x-close")
                ?.addEventListener("click", () => closeModal(modal, swiper));
        });

        // Escape key closes any open modal in this section.
        document.addEventListener("keydown", (e) => {
            if (e.key !== "Escape") return;
            const open = section.querySelector(".vc-modal[open]");
            if (open) closeModal(open, swiper);
        });
    }

    // ─── Init all uninitialised sections ────────────────────────────────────────

    function initAll() {
        document.querySelectorAll(".vc-section").forEach(initSection);
    }

    loadSwiper()
        .then(initAll)
        .catch((err) =>
            console.error("[video-carousel] Failed to load Swiper:", err),
        );

    // Handle Shopify theme editor section reloads (register once).
    if (!window.__vcSectionListener) {
        window.__vcSectionListener = true;
        document.addEventListener("shopify:section:load", () => {
            loadSwiper()
                .then(initAll)
                .catch(() => { });
        });
    }
})();
